import {
  ALL_CONTACT_TYPES,
  RELEVANCE_ORDER,
  CONTACT_FIELDS,
  openContactLink,
} from '../../../utils/contactTypeConfig';
import { IconCircleButton } from './IconCircleButton';
import { ChatCircle } from '@phosphor-icons/react/dist/ssr/ChatCircle';
import { PaperPlaneTilt } from '@phosphor-icons/react/dist/ssr/PaperPlaneTilt';

/**
 * ContactIconRow
 *
 * Quick-access row of contact icon buttons shown in Detail page headers.
 * Icons are ordered: Phone, Text (if phone shared), then by RELEVANCE_ORDER, then remaining types.
 * Excludes custom. Capped at 6 icons (5 contact + App Chat always last).
 *
 * @param {Object} contactData - Connection's contact fields (phone, email, instagram, etc.)
 * @param {string|null} inactiveStatus - Result of getInactiveStatus() ('active'|'soft_inactive'|'inactive'|null)
 * @param {function} onChatPress - Callback for app chat icon tap
 * @param {boolean} showAppChat - Append the app-chat icon (default true; the Anon
 *   lo-fi card passes false — no chat door at that tier)
 * @param {boolean} splitPhone - Render phone as call + SMS (default true; tier
 *   ruling: the split UNLOCKS at Simple — Anon surfaces show a single phone icon)
 */
const ContactIconRow = ({
  contactData = {},
  inactiveStatus,
  onChatPress,
  showAppChat = true,
  splitPhone = true,
}) => {
  const icons = [];

  // Helper to get first value for a type (handles phone/phone2 etc.)
  const getFieldValue = (type) => {
    return contactData[type] || null;
  };

  // Phone first
  const phoneValue = getFieldValue('phone');
  if (phoneValue) {
    icons.push({
      key: 'phone',
      Icon: ALL_CONTACT_TYPES.phone.Icon,
      onClick: () => openContactLink(ALL_CONTACT_TYPES.phone.getLink(phoneValue)),
    });
    // Text second (synthetic — sms: link from phone number)
    if (splitPhone) {
      icons.push({
        key: 'text',
        Icon: ChatCircle,
        onClick: () => openContactLink(`sms:${phoneValue}`),
      });
    }
  }

  // Remaining types: RELEVANCE_ORDER first (high priority), then all others except custom
  const seen = new Set(['phone', 'custom']);
  const orderedTypes = [...RELEVANCE_ORDER.filter((t) => t !== 'phone'), ...CONTACT_FIELDS];

  for (const type of orderedTypes) {
    if (seen.has(type)) continue;
    seen.add(type);
    if (icons.length >= 5) break;

    const value = getFieldValue(type);
    if (!value) continue;

    const typeConfig = ALL_CONTACT_TYPES[type];
    if (!typeConfig?.getLink) continue;

    icons.push({
      key: type,
      Icon: typeConfig.Icon,
      onClick: () => {
        const link = typeConfig.getLink(value);
        if (link) openContactLink(link);
      },
    });
  }

  // Cap contact icons at 5
  const contactIcons = icons.slice(0, 5);

  // App Chat always last
  const isActive = inactiveStatus === 'active' || inactiveStatus === null;

  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'center',
        gap: '12px',
        padding: '8px 0',
      }}
    >
      {contactIcons.map(({ key, Icon, onClick }) => (
        <IconCircleButton key={key} icon={Icon} size={22} buttonSize={40} onClick={onClick} />
      ))}
      {showAppChat && (
        <IconCircleButton
          key="app-chat"
          icon={PaperPlaneTilt}
          size={22}
          buttonSize={40}
          onClick={onChatPress}
          iconColor={isActive ? '#555555' : '#D8D8DC'}
          // Filled — the app-chat action reads as distinct from the outline contact/social icons
          iconWeight="fill"
        />
      )}
    </div>
  );
};

export default ContactIconRow;
